import { css, SerializedStyles } from '@emotion/react';
import { styled } from '@mui/material';
import { compose, flexbox, sizing, spacing } from '@mui/system';
import { FC } from 'react';
import { useTheme } from '../../../utils/MaterialUtils';
import { BrandedIcon } from '../../brandedIcon/BrandedIcon';
import { Link } from '../../navigation/link/Link';
import { Body1, Body2, Body3 } from '../../typography/TextStyles';
import { Dash } from '../dash/Dash';
import { Tooltip } from '../tooltip/Tooltip';
import { LabelFontWeight } from './model/LabelFontWeight';
import { ListItemIconTextProps } from './model/ListItemIconTextProps';

const ListStyled = styled('span')<ListItemIconTextProps>`
  ${compose(sizing, spacing, flexbox)}
  ${({ tooltip, alignItems }): SerializedStyles => css`
    display: ${tooltip ? 'inline-flex' : 'flex'};
    align-items: ${alignItems ?? 'center'};
  `}
  min-width: auto;
  max-width: 100%;
`;

const bodyStyle = ({ fontWeight }: LabelFontWeight): SerializedStyles => css`
  font-weight: ${fontWeight};
  line-height: inherit;
`;

const Body1Styled = styled(Body1)<LabelFontWeight>`
  ${bodyStyle}
`;
const Body2Styled = styled(Body2)<LabelFontWeight>`
  ${bodyStyle}
`;

const Body3Styled = styled(Body3)<LabelFontWeight>`
  ${bodyStyle}
`;

export const ListItemIconText: FC<ListItemIconTextProps> = ({
  label,
  dash = false,
  iconProps,
  children,
  alignItems = 'center',
  fontWeight = 400,
  labelProps,
  tooltip,
  tooltipPlacement,
  tooltipArrow = true,
  faulty = false,
  minWidth,
  size = 'medium',
  iconTooltip,
  width,
  noWrap = true,
  onClick,
  ...otherProps
}) => {
  const BodyStyled = size === 'large' ? Body1Styled : size === 'medium' ? Body2Styled : Body3Styled;
  const { palette } = useTheme();

  const listIcon = iconProps && (
    <BrandedIcon mb={0.5} {...iconProps}>
      {children}
    </BrandedIcon>
  );

  const listItem = (
    <ListStyled size={size} minWidth={minWidth} tooltip={tooltip} {...otherProps} alignItems={alignItems}>
      {iconTooltip ? (
        <Tooltip title={iconTooltip} placement={tooltipPlacement} arrow={tooltipArrow}>
          <div style={{ display: 'flex' }}>{listIcon}</div>
        </Tooltip>
      ) : (
        listIcon
      )}

      <BodyStyled
        component="span"
        ml={iconProps ? 0.75 : undefined}
        noWrap={noWrap}
        width={width}
        fontWeight={faulty ? 700 : fontWeight}
        color={labelProps?.color ?? palette.text.primary}
        {...labelProps}
      >
        {label || (dash && <Dash />)}
      </BodyStyled>
    </ListStyled>
  );

  const linkListItem = onClick ? (
    <Link
      hasUnderLine
      component="button"
      onClick={onClick}
      underline="hover"
      width="100%"
      color={labelProps?.color}
      textDecorationColor={labelProps?.color === 'error' ? 'error' : 'primary'}
      textAlign="left"
      sx={{ verticalAlign: 'top' }}
    >
      {listItem}
    </Link>
  ) : (
    listItem
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement={tooltipPlacement} arrow={tooltipArrow}>
      {linkListItem}
    </Tooltip>
  ) : (
    linkListItem
  );
};
